import { useEffect, useState } from 'react'
import useSWR from 'swr'
import { api, fetcher } from '../api/client'

/*
 * Sidebar / inline panel for executing AI prompts.
 * Pick template → fill variables → execute → see result, copy, optionally save as personal template.
 * If the prompt is "Email Drafting", we offer a "Send" button that opens SendEmailModal pre-filled.
 */
export default function AIToolsPanel({ prospect, onSendEmail }: {
    prospect: any
    onSendEmail?: (draft: { subject: string, body: string }) => void
}) {
    const { data: templates } = useSWR<any[]>('/api/ai-tools/templates', fetcher)
    const [tplId, setTplId] = useState<string | null>(null)
    const [vars, setVars] = useState<Record<string, string>>({})
    const [result, setResult] = useState<string | null>(null)
    const [busy, setBusy] = useState(false)
    const [err, setErr] = useState<string | null>(null)

    const tpl = templates?.find(t => t.id === tplId)

    useEffect(() => {
        if (!tpl) { setVars({}); return }
        const variableNames: string[] = JSON.parse(tpl.variablesJson || '[]')
        const next: Record<string, string> = {}
        for (const v of variableNames) next[v] = prefill(v, prospect)
        setVars(next)
    }, [tplId])

    async function execute() {
        if (!tplId) return
        setBusy(true); setErr(null); setResult(null)
        try {
            const resp: any = await api('/api/ai-tools/execute', {
                method: 'POST',
                body: JSON.stringify({ templateId: tplId, variables: vars })
            })
            setResult(resp.content)
        } catch (e: any) { setErr(e.message) }
        finally { setBusy(false) }
    }

    function copy() {
        if (result) navigator.clipboard.writeText(result)
    }

    function tryParseEmailDrafts(): { subject: string, body: string }[] | null {
        if (!result) return null
        try {
            let s = result.trim()
            if (s.startsWith('```')) {
                s = s.split('\n').slice(1).join('\n')
                if (s.endsWith('```')) s = s.slice(0, -3)
            }
            const arr = JSON.parse(s)
            if (Array.isArray(arr) && arr.length > 0 && arr[0].subject) return arr
        } catch { /* not parseable */ }
        return null
    }
    const drafts = tryParseEmailDrafts()

    return (
        <div style={{ marginTop: 12 }}>
            <div className="form-row">
                <label>Template</label>
                <select value={tplId ?? ''} onChange={e => setTplId(e.target.value || null)} data-testid="ai-template-select">
                    <option value="">— pick a template —</option>
                    {templates?.map(t => <option key={t.id} value={t.id}>{t.name} {t.isCustom ? '(custom)' : ''}</option>)}
                </select>
            </div>

            {tpl && (
                <div className="card" style={{ background: '#f9fafb' }}>
                    <strong>{tpl.name}</strong> — {tpl.promptType}
                    {Object.keys(vars).map(v => (
                        <div className="form-row" key={v}>
                            <label>{v}</label>
                            {v.toLowerCase().includes('notes') || v.toLowerCase().includes('signals') ?
                                <textarea value={vars[v]} onChange={e => setVars({ ...vars, [v]: e.target.value })} data-testid={`var-${v}`} /> :
                                <input value={vars[v]} onChange={e => setVars({ ...vars, [v]: e.target.value })} data-testid={`var-${v}`} />}
                        </div>
                    ))}
                    <button onClick={execute} disabled={busy} data-testid="execute-prompt">{busy ? 'Running...' : 'Execute'}</button>
                </div>
            )}

            {err && <div className="error">{err}</div>}

            {result && !drafts && (
                <div className="card">
                    <div className="row">
                        <strong>Result</strong>
                        <div className="grow"></div>
                        <button className="secondary" onClick={copy}>Copy</button>
                    </div>
                    <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', marginTop: 8 }}>{result}</pre>
                </div>
            )}

            {drafts && (
                <div className="card">
                    <strong>Drafts ({drafts.length})</strong>
                    {drafts.map((d, i) => (
                        <div key={i} style={{ borderTop: i > 0 ? '1px solid #e5e7eb' : 'none', paddingTop: i > 0 ? 12 : 0, marginTop: 12 }}>
                            <div><strong>Subject:</strong> {d.subject}</div>
                            <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', margin: '8px 0' }}>{d.body}</pre>
                            <div className="row">
                                <button className="secondary" onClick={() => navigator.clipboard.writeText(`${d.subject}\n\n${d.body}`)}>Copy</button>
                                {onSendEmail && <button onClick={() => onSendEmail(d)}>Send →</button>}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}

function prefill(varName: string, p: any): string {
    const map: Record<string, string> = {
        PROSPECT_NAME: p?.name ?? '',
        NAME: p?.name ?? '',
        TITLE: p?.title ?? '',
        COMPANY: p?.company ?? '',
        COMPANY_NAME: p?.company ?? '',
        WEBSITE: p?.companyWebsite ?? '',
        TONE: 'professional',
        GOAL: 'book a 15-minute intro call',
        ICEBREAKER: '',
        SIGNALS: '',
        NOTES: p?.notes ?? '',
        OBJECTION: '',
        CONTEXT: ''
    }
    return map[varName] ?? ''
}
